import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    setSelectedPatient,
    clearSelectedPatient,
    clearSearchResults,
    setTheme,
} from "../../redux/Reducer/HealthMap/HealthMapSlice";
import { searchHealthMapPatients, fetchHealthMap } from "../../redux/Thunk/HealthMap/HealthMapThunks";
import { getDoctorDetails } from "../../redux/Thunk/Doctor/DoctorThunks";
import { savePatientDetails } from "../../redux/Thunk/AdminAccount.js/AccountThunk";
import HealthMapView from "../../components/Doctor/HealthMap/HealthMapView";
import HealthMapPrescriptionForm from "../../components/Doctor/HealthMap/HealthMapPrescriptionForm";
import HealthMapReceiptForm from "../../components/Doctor/HealthMap/HealthMapReceiptForm";
import SidebarToggle from "../../components/Doctor/SidebarToggle";
import StudioIcon from "../../components/common/icons/StudioIcon";
import pincodeData from "../../utils/pincodeCityDirectory.json";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { motion, useScroll, useTransform } from "framer-motion";
import "../../components/Doctor/HealthMap/HealthMap.css";

const PlannerIcon = ({ width = 14, height = 14 }) => (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const BackgroundDecorations = () => {
    const { scrollY } = useScroll();

    // Diversified parallax speeds and rotations
    const y1 = useTransform(scrollY, [0, 3000], [0, -600]);
    const r1 = useTransform(scrollY, [0, 3000], [0, 90]);

    const y2 = useTransform(scrollY, [0, 3000], [0, -1200]);
    const r2 = useTransform(scrollY, [0, 3000], [0, -180]);

    const y3 = useTransform(scrollY, [0, 3000], [0, -400]);
    const r3 = useTransform(scrollY, [0, 3000], [0, 240]);

    const y4 = useTransform(scrollY, [0, 3000], [0, -900]);
    const r4 = useTransform(scrollY, [0, 3000], [0, -120]);

    const y5 = useTransform(scrollY, [0, 3000], [0, -700]);
    const r5 = useTransform(scrollY, [0, 3000], [0, 360]);

    const y6 = useTransform(scrollY, [0, 3000], [0, -1000]);
    const r6 = useTransform(scrollY, [0, 3000], [0, -150]);

    const y7 = useTransform(scrollY, [0, 3000], [0, -500]);
    const y8 = useTransform(scrollY, [0, 3000], [0, -800]);
    const y9 = useTransform(scrollY, [0, 3000], [0, -1500]);
    const y10 = useTransform(scrollY, [0, 3000], [0, -450]);
    const y11 = useTransform(scrollY, [0, 3000], [0, -1100]);
    const y12 = useTransform(scrollY, [0, 3000], [0, -750]);

    return (
        <div className="hm-bg-decorations" aria-hidden="true">
            {/* Top Section */}
            <motion.div className="hm-decor-item plus" style={{ top: '8%', left: '4%', y: y1, rotate: r1 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M12 5v14M5 12h14" /></svg>
            </motion.div>
            <motion.div className="hm-decor-item stethoscope" style={{ top: '15%', left: '85%', y: y2, rotate: r2, width: 60, height: 60 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M4.8 2.3c.5.2.9.7.9 1.2v3c0 3.5 2.5 6.5 6 6.5s6-3 6-6.5v-3c0-.5.4-1 1-1.2m-13.9 0c.5.2.9.7.9 1.2v3c0 3.5 2.5 6.5 6 6.5s6-3 6-6.5v-3c0-.5.4-1 1-1.2" /><path d="M12 13v4m0 0a3.5 3.5 0 100 7 3.5 3.5 0 000-7z" /></svg>
            </motion.div>
            <motion.div className="hm-decor-item dna" style={{ top: '25%', left: '12%', y: y3, rotate: r3, width: 50, height: 50 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M4 2c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3zm16 16c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3zM7 5h10M7 19h10M10 9h4M10 15h4m3-10c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3zM4 16c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3z" /></svg>
            </motion.div>

            {/* Mid Section */}
            <motion.div className="hm-decor-item rx" style={{ top: '38%', left: '92%', y: y4, rotate: r4 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 00 2 2h12a2 2 0 00 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
            </motion.div>
            <motion.div className="hm-decor-item pill" style={{ top: '45%', left: '5%', y: y5, rotate: r5, width: 45, height: 45 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M10.5 1.5l-8.46 8.46a5 5 0 007.08 7.04l8.46-8.46a5 5 0 00-7.08-7.04zM6.05 13.54l5.5-5.5" /></svg>
            </motion.div>
            <motion.div className="hm-decor-item shield" style={{ top: '55%', left: '88%', y: y6, rotate: r6 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            </motion.div>

            {/* Deep Section */}
            <motion.div className="hm-decor-item molecule" style={{ top: '65%', left: '15%', y: y7, rotate: 15, width: 70, height: 70 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><circle cx="12" cy="12" r="3" /><circle cx="19" cy="5" r="2" /><circle cx="5" cy="19" r="2" /><circle cx="19" cy="19" r="2" /><circle cx="5" cy="5" r="2" /><line x1="12" y1="12" x2="19" y2="5" /><line x1="12" y1="12" x2="5" y2="19" /><line x1="12" y1="12" x2="19" y2="19" /><line x1="12" y1="12" x2="5" y2="5" /></svg>
            </motion.div>
            <motion.div className="hm-decor-item thermometer" style={{ top: '75%', left: '90%', y: y8, rotate: -30 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M14 4v10.54a4 4 0 11-4 0V4a2 2 0 114 0z" /></svg>
            </motion.div>
            <motion.div className="hm-decor-item heart" style={{ top: '82%', left: '8%', y: y9, rotate: 10, width: 55, height: 55 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>
            </motion.div>

            {/* Bottom Section Extra */}
            <motion.div className="hm-decor-item plus" style={{ top: '92%', left: '84%', y: y10, rotate: 45 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M12 5v14M5 12h14" /></svg>
            </motion.div>
            <motion.div className="hm-decor-item medical-bag" style={{ top: '95%', left: '40%', y: y11, rotate: -5, width: 65, height: 65 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /><line x1="12" y1="11" x2="12" y2="16" /><line x1="9.5" y1="13.5" x2="14.5" y2="13.5" /></svg>
            </motion.div>
            <motion.div className="hm-decor-item syringe" style={{ top: '88%', left: '70%', y: y12, rotate: 135 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="m18 2 4 4" /><path d="m17 7 3-3" /><path d="M19 9 8.7 19.3c-1 1-2.5 1-3.4 0l-.6-.6c-1-1-1-2.5 0-3.4L15 5" /><path d="m9 11 4 4" /><path d="m5 19-3 3" /><path d="m14 4 6 6" /></svg>
            </motion.div>
        </div>
    );
};

const HealthMap = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const selectedPatient = useSelector((s) => s.healthMap.selectedPatient);
    const searchResults = useSelector((s) => s.healthMap.searchResults);
    const searchLoading = useSelector((s) => s.healthMap.searchLoading);
    const theme = useSelector((s) => s.healthMap.theme);
    const activeOverlay = useSelector((s) => s.healthMap.activeOverlay);

    const [searchQuery, setSearchQuery] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);
    const debounceRef = useRef(null);

    // New Patient Form State
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newPatient, setNewPatient] = useState({
        name: "",
        surname: "",
        phoneNumber: "",
        gender: "",
        age: "",
        dateofbirth: "",
        pincode: "",
        city: "",
    });
    const [ageMode, setAgeMode] = useState(false);
    const [cityMode, setCityMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pincodeSuggestions, setPincodeSuggestions] = useState([]);
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [showGenderDropdown, setShowGenderDropdown] = useState(false);
    const genderRef = useRef(null);

    const currentDID = useSelector((s) => s?.doctor?.doctorDetails?.doctor?.DID);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token && !currentDID) {
            dispatch(getDoctorDetails(token));
        }
    }, [dispatch, currentDID]);


    const calculateAge = (dob) => {
        if (!dob) return null;
        const birthDate = new Date(dob);
        if (isNaN(birthDate.getTime())) return null;
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // Debounced search
    const handleSearchChange = useCallback((e) => {
        const q = e.target.value;
        setSearchQuery(q);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (q.trim().length >= 2) {
            debounceRef.current = setTimeout(() => {
                dispatch(searchHealthMapPatients(q.trim()));
                setShowDropdown(true);
            }, 400);
        } else {
            dispatch(clearSearchResults());
            setShowDropdown(false);
        }
    }, [dispatch]);

    // Click outside to close dropdown
    useEffect(() => {
        const handler = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
            if (genderRef.current && !genderRef.current.contains(e.target)) {
                setShowGenderDropdown(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handlePatientSelect = (patient) => {
        dispatch(setSelectedPatient(patient));
        dispatch(fetchHealthMap(patient.PID));
        setShowDropdown(false);
        setSearchQuery("");
        dispatch(clearSearchResults());
        window.scrollTo(0, 0);
    };

    // Auto-select patient from context
    useEffect(() => {
        const raw = sessionStorage.getItem("healthmap-patient-context");
        if (raw) {
            try {
                const patient = JSON.parse(raw);
                if (patient && patient.PID) {
                    sessionStorage.removeItem("healthmap-patient-context");
                    handlePatientSelect(patient);
                }
            } catch (err) {
                console.error("Error parsing healthmap-patient-context", err);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleChangePatient = () => {
        dispatch(clearSelectedPatient());
        setSearchQuery("");
        setIsAddingNew(false);
    };

    const handleGoToStudio = () => {
        if (selectedPatient) {
            sessionStorage.setItem("healthmap-patient-context", JSON.stringify(selectedPatient));
            navigate("/studio");
        }
    };

    const handleGoToPlanner = () => {
        navigate("/planner");
    };

    const handleStartAdding = () => {
        const isNumeric = /^\d+$/.test(searchQuery);
        const isPhone = /^\d{10}$/.test(searchQuery);

        setNewPatient({
            ...newPatient,
            phoneNumber: isPhone ? searchQuery : "",
            name: !isNumeric ? searchQuery : "",
            surname: "",
            gender: "",
            age: "",
            dateofbirth: "",
            pincode: "",
            city: "",
        });
        setIsAddingNew(true);
        setShowDropdown(false);
    };

    const handleSaveNewPatient = async () => {
        if (!newPatient.name || !newPatient.surname || !newPatient.phoneNumber || !newPatient.gender) {
            toast.error("Please fill all required fields");
            return;
        }
        if (ageMode && !newPatient.age) {
            toast.error("Please enter age");
            return;
        }
        if (!ageMode && !newPatient.dateofbirth) {
            toast.error("Please enter date of birth");
            return;
        }
        if (cityMode && !newPatient.city) {
            toast.error("Please enter city");
            return;
        }
        if (!cityMode && !newPatient.pincode) {
            toast.error("Please enter pincode");
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                patient: newPatient,
                DID: currentDID
            };
            const response = await dispatch(savePatientDetails(payload)).unwrap();
            const savedPatient = response.data;
            toast.success("Patient created successfully");
            handlePatientSelect(savedPatient);
        } catch (err) {
            toast.error(err.message || "Failed to create patient");
        } finally {
            setIsSaving(false);
        }
    };

    const toggleTheme = () => {
        dispatch(setTheme(theme === "light" ? "dark" : "light"));
    };

    return (
        <div className="healthmap-page" data-theme={theme}>
            <BackgroundDecorations />

            {/* Phase 1: Patient Search */}
            {!selectedPatient && (
                <div className="search-phase">
                    <div className="search-phase-controls">
                        <SidebarToggle />
                        <div className="search-theme-wrapper">
                            <span className="theme-icon">{theme === "light" ? "☀️" : "🌙"}</span>
                            <button className="theme-toggle" onClick={toggleTheme} />
                        </div>
                    </div>
                    <div className="search-title">Patient HealthMap</div>
                    <div className="search-subtitle">Search for a patient to view their complete health journey</div>
                    <div className="search-container" ref={searchRef}>
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name or phone number..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onFocus={() => (searchResults.length > 0 || searchQuery.length >= 2) && setShowDropdown(true)}
                            autoFocus
                        />

                        {showDropdown && (
                            <div className="search-dropdown">
                                {searchLoading ? (
                                    <div className="search-loading">Searching...</div>
                                ) : searchResults.length > 0 ? (
                                    searchResults.map((p) => (
                                        <div
                                            key={p.PID}
                                            className="search-item"
                                            onClick={() => handlePatientSelect(p)}
                                        >
                                            <div>
                                                <div className="search-item-name">
                                                    {p.name} {p.surname}
                                                </div>
                                                {p.city && (
                                                    <div className="search-item-city">{p.city}</div>
                                                )}
                                            </div>
                                            <div className="search-item-phone">{p.phoneNumber}</div>
                                        </div>
                                    ))
                                ) : searchQuery.length >= 2 ? (
                                    <div className="search-no-results">
                                        <div className="search-loading">No patients found</div>
                                        {(/^\d+$/.test(searchQuery) ? searchQuery.length === 10 : true) && (
                                            <button className="add-patient-prompt" onClick={handleStartAdding}>
                                                ✨ Add New Patient: <strong>{searchQuery}</strong>
                                            </button>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>

                    {isAddingNew && (
                        <div className="new-patient-form-container">
                            <div className="new-patient-form">
                                <div className="form-header">
                                    <h3>New Patient Details</h3>
                                    <button className="close-form" onClick={() => setIsAddingNew(false)}>✕</button>
                                </div>
                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>Contact Number</label>
                                        <input
                                            type="number"
                                            value={newPatient.phoneNumber}
                                            onChange={(e) => setNewPatient({ ...newPatient, phoneNumber: e.target.value })}
                                            placeholder="Contact"
                                        />
                                    </div>
                                    <div className="form-field" ref={genderRef}>
                                        <label>Gender</label>
                                        <div className="custom-dropdown">
                                            <div
                                                className={`dropdown-selected ${newPatient.gender ? 'has-value' : ''}`}
                                                onClick={() => setShowGenderDropdown(!showGenderDropdown)}
                                            >
                                                {newPatient.gender || "Select"}
                                                <span className={`dropdown-arrow ${showGenderDropdown ? 'open' : ''}`}>▼</span>
                                            </div>
                                            {showGenderDropdown && (
                                                <div className="dropdown-options">
                                                    {["Male", "Female", "Other"].map(g => (
                                                        <div
                                                            key={g}
                                                            className={`dropdown-option ${newPatient.gender === g ? 'active' : ''}`}
                                                            onClick={() => {
                                                                setNewPatient({ ...newPatient, gender: g });
                                                                setShowGenderDropdown(false);
                                                            }}
                                                        >
                                                            {g}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-field">
                                        <label>First Name</label>
                                        <input
                                            type="text"
                                            value={newPatient.name}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (!/\d/.test(val)) {
                                                    setNewPatient({ ...newPatient, name: val });
                                                }
                                            }}
                                            placeholder="First Name"
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Surname</label>
                                        <input
                                            type="text"
                                            value={newPatient.surname}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (!/\d/.test(val)) {
                                                    setNewPatient({ ...newPatient, surname: val });
                                                }
                                            }}
                                            placeholder="Surname"
                                        />
                                    </div>
                                    <div className="form-field">
                                        <div className="field-header">
                                            <label>{ageMode ? "Age" : "Date of Birth"}</label>
                                            <button className="swap-btn" onClick={() => setAgeMode(!ageMode)}>Swap</button>
                                        </div>
                                        {ageMode ? (
                                            <input
                                                type="number"
                                                value={newPatient.age}
                                                onChange={(e) => setNewPatient({ ...newPatient, age: e.target.value, dateofbirth: "" })}
                                                placeholder="Age"
                                            />
                                        ) : (
                                            <input
                                                type="date"
                                                value={newPatient.dateofbirth}
                                                onChange={(e) => setNewPatient({ ...newPatient, dateofbirth: e.target.value, age: "" })}
                                            />
                                        )}
                                    </div>
                                    <div className="form-field">
                                        <div className="field-header">
                                            <label>{cityMode ? "City" : "Pincode"}</label>
                                            <button className="swap-btn" onClick={() => setCityMode(!cityMode)}>Swap</button>
                                        </div>
                                        {!cityMode ? (
                                            <div className="suggest-input">
                                                <input
                                                    type="number"
                                                    value={newPatient.pincode}
                                                    onChange={(e) => {
                                                        const q = e.target.value;
                                                        setNewPatient({ ...newPatient, pincode: q, city: "" });
                                                        if (q.length >= 3) {
                                                            const match = pincodeData.filter(d => String(d.pincode).startsWith(q)).slice(0, 20);
                                                            setPincodeSuggestions(match);
                                                        } else setPincodeSuggestions([]);
                                                    }}
                                                    placeholder="Pincode"
                                                />
                                                {pincodeSuggestions.length > 0 && (
                                                    <div className="mini-suggestions">
                                                        {pincodeSuggestions.map((s, i) => (
                                                            <div key={i} onClick={() => {
                                                                setNewPatient({ ...newPatient, pincode: s.pincode, city: s.city });
                                                                setPincodeSuggestions([]);
                                                            }}>
                                                                <strong>{s.pincode}</strong> - {s.area}, {s.city}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="suggest-input">
                                                <input
                                                    type="text"
                                                    value={newPatient.city}
                                                    onChange={(e) => {
                                                        const q = e.target.value;
                                                        setNewPatient({ ...newPatient, city: q, pincode: "" });
                                                        if (q.length >= 3) {
                                                            const match = pincodeData.filter(d => d.city.toLowerCase().includes(q.toLowerCase())).slice(0, 20);
                                                            setCitySuggestions(match);
                                                        } else setCitySuggestions([]);
                                                    }}
                                                    placeholder="City"
                                                />
                                                {citySuggestions.length > 0 && (
                                                    <div className="mini-suggestions">
                                                        {citySuggestions.map((s, i) => (
                                                            <div key={i} onClick={() => {
                                                                setNewPatient({ ...newPatient, city: s.city, pincode: s.pincode });
                                                                setCitySuggestions([]);
                                                            }}>
                                                                <strong>{s.city}</strong> ({s.pincode}) - {s.area}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    className="save-patient-btn"
                                    onClick={handleSaveNewPatient}
                                    disabled={isSaving}
                                >
                                    {isSaving ? "Saving..." : "Save Patient Details"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Phase 2: Patient Selected — Show Banner + HealthMap */}
            {selectedPatient && (
                <>
                    <div className="patient-banner">
                        <div className="patient-banner-left">
                            <SidebarToggle />
                            <div className="patient-banner-main">
                                <h2 className="patient-banner-name">
                                    {selectedPatient.name} {selectedPatient.surname}
                                </h2>
                                <div className="patient-banner-tags">
                                    {selectedPatient.gender && <span className="patient-tag gender">{selectedPatient.gender}</span>}
                                    {(selectedPatient.age || calculateAge(selectedPatient.dateofbirth)) && (
                                        <span className="patient-tag age">
                                            {selectedPatient.age || calculateAge(selectedPatient.dateofbirth)} Years
                                        </span>
                                    )}
                                    <span className="patient-tag phone">📞 {selectedPatient.phoneNumber}</span>
                                </div>
                            </div>
                        </div>

                        <div className="patient-banner-right">
                            <div className="patient-location">
                                <span className="location-icon">📍</span>
                                <div className="location-details">
                                    <div className="location-city">{selectedPatient.city || "No City"}</div>
                                    <div className="location-pin">{selectedPatient.pincode || "No Pincode"}</div>
                                </div>
                            </div>
                            <div className="patient-banner-actions">
                                <div className="banner-2x2-matrix">
                                    <button className="matrix-btn" onClick={handleChangePatient}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
                                        </svg>
                                        Change Patient
                                    </button>

                                    <button className="matrix-btn studio-jump-btn" onClick={handleGoToStudio}>
                                        <StudioIcon width={14} height={14} />
                                        Go To Studio
                                    </button>

                                    <button className="matrix-btn theme-matrix-btn" onClick={toggleTheme}>
                                        <span>{theme === "light" ? "☀️ Light Mode" : "🌙 Dark Mode"}</span>
                                    </button>

                                    <button className="matrix-btn planner-jump-btn" onClick={handleGoToPlanner}>
                                        <PlannerIcon width={14} height={14} />
                                        Go To Planner
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <HealthMapView key={selectedPatient?.PID} />
                </>
            )}

            {/* Overlays */}
            {activeOverlay === "prescription" && <HealthMapPrescriptionForm />}
            {activeOverlay === "receipt" && <HealthMapReceiptForm />}
        </div>
    );
};

export default HealthMap;
